import { Component, Input, Output, EventEmitter, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CONSTANTS_TOKEN } from '../../../config';
import { PortalService, UtilsService } from '../../../services';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { SnackBar } from '../../../enums';
import { DocumentItem } from '../document.model';

@Component({
  selector: 'educare-document-viewer',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslateModule, PdfViewerModule],
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent implements OnInit {
  @Input() document!: DocumentItem;
  @Output() back = new EventEmitter<void>();
  @Output() download = new EventEmitter<DocumentItem>();

  documentUrl: SafeResourceUrl = '';
  thumbnailUrl: SafeResourceUrl = '';
  pdfUrl: string = '';
  loading = true;
  error = false;
  documentPreview: any = null; // Store preview data
  isSupported = false;
  iframeError = false;
  showIframe = false;

  private readonly SUPPORTED_EXTENSIONS = ['pdf', 'docx', 'doc', 'xls', 'xlsx'];

  constructor(
    @Inject(CONSTANTS_TOKEN) private constants: any,
    private utils: UtilsService,
    private sanitizer: DomSanitizer,
    private portalService: PortalService
  ) {}

  ngOnInit() {
    this.loadDocument();
  }

  loadDocument() {
    if (!this.document?.id) {
      this.error = true;
      this.loading = false;
      return;
    }

    const previewUrl = this.utils.getEncryptedUrls(
      this.constants.api.privateEndpoints.knowledgeHub,
      `knowledge-hub/documents/${this.document.id}/preview`
    );

    this.portalService.get(previewUrl).subscribe({
      next: (response: any) => {
        this.documentPreview = response.data;
        this.processDocumentPreview();
      },
      error: (error: any) => {
        this.utils.openSnackBar(error.error.message, 'Close', SnackBar.Error);
        this.loading = false;
      }
    });
  }

  private processDocumentPreview() {
    if (!this.documentPreview) {
      this.error = true;
      this.loading = false;
      return;
    }

    const fileExtension = this.getFileExtension(this.documentPreview.name);
    this.isSupported = this.SUPPORTED_EXTENSIONS.includes(fileExtension);

    if (this.isSupported && this.documentPreview.previewUrl) {
      this.documentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.documentPreview.previewUrl);
      this.showIframe = true;
    } else if (this.isSupported && !this.documentPreview.previewUrl) {
      this.isSupported = false;
      this.showIframe = false;

      if (this.documentPreview.thumbnailUrl) {
        this.thumbnailUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.documentPreview.thumbnailUrl);
      }
    } else if (this.documentPreview.thumbnailUrl) {
      this.thumbnailUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.documentPreview.thumbnailUrl);
      this.showIframe = false;
    }

    this.loading = false;
  }

  private getFileExtension(fileName: string): string {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
  }

  onBack() {
    this.back.emit();
  }

  onDownload() {
    if (this.document?.id) {
      this.utils.spin.show();
      const downloadUrl = this.utils.getEncryptedUrls(
        this.constants.api.privateEndpoints.knowledgeHub,
        `knowledge-hub/documents/${this.document.id}/download`
      );

      this.portalService
        .get(downloadUrl, {
          observe: 'response',
          responseType: 'blob'
        })
        .subscribe({
          next: (response: any) => {
            let fileName = this.documentPreview?.name || this.document.name || 'document';

            if (!fileName.includes('.')) {
              const extension =
                this.utils.mimeTypeToExtension(this.documentPreview?.mimeType || response.body.type) || '';
              fileName = `${fileName}${extension}`;
            }

            this.utils.downLoadFile(response.body, response.body.type, fileName);
            this.utils.spin.hide();
          },
          error: async (error: any) => {
            this.utils.spin.hide();
            this.utils.openSnackBar(error.error.message, 'Close', SnackBar.Error);
          }
        });
    }
  }

  openFullDocument() {
    if (this.documentPreview?.webUrl) {
      window.open(this.documentPreview.webUrl, '_blank');
    }
  }

  onIframeError() {
    this.iframeError = true;
    this.showIframe = false;
  }

  onIframeLoad() {
    this.iframeError = false;
  }
}
